import { Platform, type TextStyle } from 'react-native';

import type { ChildBand } from '../a11y';

/** Poppins only. Weights the board uses. */
export type PoppinsWeight = 400 | 500 | 600 | 700 | 800;

const nativeFamily: Record<PoppinsWeight, string> = {
  400: 'Poppins_400Regular',
  500: 'Poppins_500Medium',
  600: 'Poppins_600SemiBold',
  700: 'Poppins_700Bold',
  800: 'Poppins_800ExtraBold',
};

/** Native loads one file per weight (fontFamily carries the weight); web uses the family + CSS weight. */
export function fontStyle(weight: PoppinsWeight): TextStyle {
  return Platform.OS === 'web'
    ? { fontFamily: 'Poppins', fontWeight: String(weight) as TextStyle['fontWeight'] }
    : { fontFamily: nativeFamily[weight] };
}

export const lineHeights = { headline: 1.15, body: 1.5 } as const;

export interface TypeStyle {
  size: number;
  weight: PoppinsWeight;
  lineHeight: number;
  letterSpacingEm?: number;
  uppercase?: boolean;
}

/**
 * Parent-mode scale (README: body 13–15, titles 24–26/800, eyebrows 11–12 caps .14em).
 * Body never drops under 13; secondary lines use `meta` (12) or `label` (12/600).
 */
export const parentType = {
  /** Intro headline (P01 board: 30/800 white). Sits above the README's 24–26 parent title range — the board wins; recorded in plan Step 5. */
  heroTitle: { size: 30, weight: 800, lineHeight: lineHeights.headline },
  title: { size: 26, weight: 800, lineHeight: lineHeights.headline },
  /** Kid-card score number (30/800). */
  score: { size: 30, weight: 800, lineHeight: 1.1 },
  title2: { size: 24, weight: 800, lineHeight: lineHeights.headline },
  cardTitle: { size: 17, weight: 700, lineHeight: lineHeights.headline },
  /**
   * The 17/800 slot — `cardTitle`'s size at a title's weight. Named for its first use, C17's lock-glyph heading
   * above the PIN dots; T03's rail title (the family's name over the destinations) is the second. The name is
   * that first screen's accident and is left alone rather than fixed into a third near-duplicate weight.
   */
  padHeading: { size: 17, weight: 800, lineHeight: lineHeights.headline },
  /** 1a approvals card head "3 to approve" (board 20/800). */
  cardTitleLg: { size: 20, weight: 800, lineHeight: lineHeights.headline },
  /** On-canvas section heading "Your kids" (board 18/700 white). */
  sectionHeading: { size: 18, weight: 700, lineHeight: lineHeights.headline },
  sectionTitle: {
    size: 12,
    weight: 700,
    lineHeight: lineHeights.body,
    letterSpacingEm: 0.1,
    uppercase: true,
  },
  body: { size: 15, weight: 500, lineHeight: lineHeights.body },
  /** A guide's words (board P35b, 28 Sep: "the guide's words at 17px"). */
  reading: { size: 17, weight: 500, lineHeight: lineHeights.body },
  bodySm: { size: 13, weight: 500, lineHeight: lineHeights.body },
  /** S / M / L letter on the P05 size tile (board 12/800). */
  sizeLetter: { size: 12, weight: 800, lineHeight: lineHeights.body },
  /** Bold lead-in inside a 13px body run (P08 “Read aloud to Ruby:”). */
  bodySmBold: { size: 13, weight: 700, lineHeight: lineHeights.body },
  helper: { size: 13, weight: 500, lineHeight: lineHeights.body },
  label: { size: 12, weight: 600, lineHeight: lineHeights.body },
  /** Active tab label (1a/P10). */
  labelStrong: { size: 12, weight: 700, lineHeight: lineHeights.body },
  /**
   * **T03's LEFT-RAIL destination, 16/600 and 16/800 for the one you are on** — the parent's tablet nav, and
   * the only parent text in the register above 15.
   *
   * It is deliberately NOT `label` at 12. The bottom bar's label sits under a 56pt disc that carries the
   * meaning, so 12 is a caption on a glyph; the rail has no disc, so the WORD is the destination, on a screen
   * held at arm's length. The board draws 16 and the reason is the same one that made the rail wide.
   */
  railLabel: { size: 16, weight: 600, lineHeight: lineHeights.body },
  railLabelStrong: { size: 16, weight: 800, lineHeight: lineHeights.body },
  meta: { size: 12, weight: 500, lineHeight: lineHeights.body },
  /** Emphasised meta: the magenta reward-request line (board 12/600). */
  metaStrong: { size: 12, weight: 600, lineHeight: lineHeights.body },
  eyebrow: {
    size: 12,
    weight: 600,
    lineHeight: lineHeights.body,
    letterSpacingEm: 0.14,
    uppercase: true,
  },
  /** Header step line "Step 1 of 3" (13/600 on turquoise). */
  overline: { size: 13, weight: 600, lineHeight: lineHeights.body },
  /** Row titles on cards: ToggleRow, ApprovalRow (15/700). */
  rowTitle: { size: 15, weight: 700, lineHeight: lineHeights.headline },
  /** Inbox row titles (14/700). */
  rowTitleSm: { size: 14, weight: 700, lineHeight: lineHeights.headline },
  /** Times on inbox rows (11, muted). */
  time: { size: 11, weight: 500, lineHeight: lineHeights.body },
  /** Sticky CTA labels (md). */
  button: { size: 17, weight: 700, lineHeight: lineHeights.headline },
  /** Inline sm CTAs ("Approve all", "Done"). */
  buttonSm: { size: 14, weight: 700, lineHeight: lineHeights.headline },
  /** Outline buttons (P02 social, P08 "Not yet"). */
  buttonOutline: { size: 15, weight: 700, lineHeight: lineHeights.headline },
  /** Half-width outline pair (P06 Apple / Google: 14/700). */
  buttonOutlineSm: { size: 14, weight: 700, lineHeight: lineHeights.headline },
  /** Text links under a CTA ("Add another child", "Approve and add a note"). */
  textLink: { size: 14, weight: 700, lineHeight: lineHeights.body },
  /** Small inline links (P06 "Forgot password?", "Create a family": 13/700). */
  textLinkSm: { size: 13, weight: 700, lineHeight: lineHeights.body },
  /** P02 legal line (11, muted, links underlined in the same muted colour). */
  legal: { size: 11, weight: 500, lineHeight: lineHeights.body },
  /** The larger text link on the gradient ("I already have an account"). */
  textLinkLg: { size: 15, weight: 600, lineHeight: lineHeights.body },
  /** Typed values in text fields and picker rows. */
  input: { size: 15, weight: 600, lineHeight: lineHeights.body },
  /** Filter chips. */
  chip: { size: 13, weight: 600, lineHeight: lineHeights.body },
  chipSelected: { size: 13, weight: 700, lineHeight: lineHeights.body },
  /**
   * Rewards-fund money runs (P13b–P13d). Dollars are parent mode only (CLAUDE.md), so these live on the
   * parent scale and have no child counterpart. Board: P13c `:549` · P13b `:538` · P13d `:574`.
   */
  /** P13c balance "$23.50" (44/800, line-height 1.05). */
  balance: { size: 44, weight: 800, lineHeight: 1.05 },
  /** P13c ADD MONEY tile "$25" (18/800). P13b's tile is 20/800 → `cardTitleLg`. */
  moneyMd: { size: 18, weight: 800, lineHeight: lineHeights.headline },
  /** P13c "Other" tile (15/800, muted), P13 score chip "80" (15/800). */
  moneySm: { size: 15, weight: 800, lineHeight: lineHeights.headline },
  /** P13c "Funded rewards waiting" total, P13d "100 BB" / "$10.00" (16/800). */
  figure: { size: 16, weight: 800, lineHeight: lineHeights.headline },
  /** P13c history amounts, signed and coloured by sign (14/800). */
  figureSm: { size: 14, weight: 800, lineHeight: lineHeights.headline },
  /** P13b amount-tile BB sub-line "100 BB", C04 "Saving · 504 of 800" (11/700). */
  amountMeta: { size: 11, weight: 700, lineHeight: lineHeights.body },
  /** Badges ("NEW", "FROM DEVICE", "BGC"). */
  badge: {
    size: 11,
    weight: 700,
    lineHeight: lineHeights.body,
    letterSpacingEm: 0.06,
    uppercase: true,
  },
} as const satisfies Record<string, TypeStyle>;

/**
 * Child-mode scale (README: body 16–17, titles 24–30/800). The runs were first transcribed from the reader
 * artboards C04, C05, C12–C15, C18 and C19. SEVENTEEN of them have since been raised OFF the board, two more
 * (`tileCaptionSm` 9/700, `discTagSm` 10/800) deleted into their larger siblings, and two added for the child
 * tab bar (`navLabel`, `navLabelStrong`) — because the board is wrong and Evan is fixing it at the source:
 * "10px captions on a child screen is a defect I'm fixing at the source; don't transcribe it faithfully."
 *
 * The rule that replaces the board on these runs: a **14px floor for micro-labels** (nav labels, axis ticks,
 * tags) and **16px for anything the child reads as content**. His swept board is NOT in this repo — `docs/
 * handoff/design/Kiddie Kredit Redesign Board.dc.html` is unchanged since `c75d289` and still carries the old
 * 10px/9px values — so do not "verify" a size against it and do not lower one back to match it. Every raised
 * run therefore keeps its ORIGINAL board citation and records the SHIPPED number beside it, so the sweep can be
 * diffed against this file when it lands.
 *
 * THERE IS NO LONGER AN EXCEPTION. `label` was the last run under the floor (13, C12's mission pill) and it is
 * now 16: Evan fixed the C12 pill at the source — "anchored 24px off the right edge, wraps instead of running
 * off screen, and its caption is 16px" — so the size and the layout that blocked it landed together. Every run
 * in this table is now ≥ 14, every run the rule reads as CONTENT is at 16, and `typography.test.ts` asserts the
 * floor with an EMPTY exception set. The four content runs that were still at 15 — `messageTitle`,
 * `instruction`, `link`, `linkStrong` — were raised in round 5 (the C19 hierarchy had inverted without them),
 * `buttonSm` followed in round 6, and `label` closes it in round 7. `prereaderType` stays untouched: its table
 * is empty for C12–C15/C18/C19 and extrapolating it is forbidden.
 *
 * Reach this scale through `ChildText`, never through `Text` (which is the parent scale).
 */
export const childType = {
  /** C15 headline "You saved for the scooter!", C11 header "Rewards" (30/800). */
  heroTitle: { size: 30, weight: 800, lineHeight: lineHeights.headline },
  /** C14 question "Which one is a need?" (28/800/1.2), C04 header "Rewards" (28/800). */
  title: { size: 28, weight: 800, lineHeight: 1.2 },
  /**
   * C01's and C09's score number — **52/800 on the redrawn boards (2026-09-22), up from C28's 40.**
   *
   * Bigger than any other child run on purpose: *"the score is the hero"*, and the size gap is what says which
   * of the two figures on that line is the figure. Both bands draw it at 52, which is why `prereaderType` does
   * not override it — a pre-reader's card carries fewer things, not smaller ones.
   */
  score: { size: 52, weight: 800, lineHeight: 1.1 },
  /**
   * The band word beside that number. **A PILL on the redrawn card (15/800, ink on the band's own well)**
   * rather than C28's muted 20/800 word — the band is a name for where you are, and a pill is how this app
   * draws one everywhere else. SHIPPED at 16, the content floor.
   */
  scoreBand: { size: 16, weight: 800, lineHeight: lineHeights.headline },
  /** C18 "My badges", C19 "Messages", C05 hero "Movie night pick" (26/800). */
  title2: { size: 26, weight: 800, lineHeight: lineHeights.headline },
  /** C12 "Bamboo Trail" (22/800/1.1), C11 BB pill "126", C11 locked tile "80". */
  title3: { size: 22, weight: 800, lineHeight: 1.1 },
  /** C05 "Your request" (20/800). */
  title4: { size: 20, weight: 800, lineHeight: lineHeights.headline },
  /** C05 redemption code "KK-4821" (44/800, .12em, line-height 1). */
  code: { size: 44, weight: 800, lineHeight: 1, letterSpacingEm: 0.12 },
  /** C13 story prompt (20/700/1.35) — the one child run set at 700, not 800. */
  prompt: { size: 20, weight: 700, lineHeight: 1.35 },
  /** C13 "What should Kai do?" (18/800), C11 tile title (18/800). */
  ask: { size: 18, weight: 800, lineHeight: lineHeights.headline },
  /** C14 quiz tile caption (17/800). */
  caption: { size: 17, weight: 800, lineHeight: lineHeights.headline },
  /** C05 "Mom or Dad, confirm here" (17/800), C11 "Get" is 18/800 → use `ask`. */
  button: { size: 17, weight: 800, lineHeight: lineHeights.headline },
  /** C12 "Start mission 4", C15 "Back to the trail" (19/800). */
  buttonLg: { size: 19, weight: 800, lineHeight: lineHeights.headline },
  /**
   * C14 "Next" and C04 "Get it" — the child's CTA TEXT (board 14/800, and 14/700 for "Get it" → the weight of
   * `pillQuiet`; SHIPPED 16/800, content floor; line box 16→18).
   *
   * RAISED, not filed. Evan's rule names three things as micro-labels — nav labels, axis ticks, tags — and a CTA
   * label is none of them: it is the one word on the screen the child reads in order to act, which is content by
   * any reading of the rule. It was also the only run left on a child CTA at the micro floor while `button` (17),
   * `buttonLg` (19) and `prereaderType.buttonSm` (18) sat well above it.
   *
   * Nothing blocks the raise, which is why this is not a second `label`: `childButtonFace('sm')` already draws
   * the face at `childHit` (48 reader / 56 pre-reader) rather than the board's 44, so the taller line box has
   * room, and the one call site (`QuestFooterCard`) hugs its label inside a flex row — "Next" grows ~5pt into
   * the card's own slack instead of pushing anything off the edge.
   */
  buttonSm: { size: 16, weight: 800, lineHeight: lineHeights.headline },
  /** Child body (README 17). C14 instruction "Tap the picture." is 15/500 → use `meta`-adjacent `label`. */
  body: { size: 17, weight: 500, lineHeight: lineHeights.body },
  bodySm: { size: 16, weight: 500, lineHeight: lineHeights.body },
  /** C13 choice label (16/700/1.3). */
  choice: { size: 16, weight: 700, lineHeight: 1.3 },
  /** C15 stat rows, C15 "New badge · 7 of 24" (16/700). */
  rowTitle: { size: 16, weight: 700, lineHeight: lineHeights.headline },
  /** C14 feedback title "Yes! Dinner is a need.", C05 "150 BB on hold" (16/800). */
  rowTitleBold: { size: 16, weight: 800, lineHeight: lineHeights.headline },
  /**
   * C19 row title, C04 tile title (board 15/700 — SHIPPED 16/700, content floor; line box 17→18). A row
   * headline is the most-read string on C19, and `messageBody` under it went to 16 as content: at 15 the
   * muted secondary line drew LARGER than the bold title above it. Raised with the three runs below so the
   * hierarchy is right side up again (`typography.test.ts` pins it).
   */
  messageTitle: { size: 16, weight: 700, lineHeight: lineHeights.headline },
  /** C19 row body, C04 org meta, C05 "Good until Sun · one use" (board 12/500 — SHIPPED 16/500, content floor; line box 18→24). */
  messageBody: { size: 16, weight: 500, lineHeight: lineHeights.body },
  /** C12 "6 badges" pill (14/800). C12's "Mission 4 · …" node label is 13/700 → `label`. */
  pill: { size: 14, weight: 800, lineHeight: lineHeights.headline },
  /** C05 status pill "Waiting for a grown-up" (14/700). */
  pillQuiet: { size: 14, weight: 700, lineHeight: lineHeights.headline },
  /**
   * C12's curriculum level line, under the current mission's pill — "Character Traits" (Evan, 2026-09-18:
   * *"14px in the secondary slate"*, drawn `tone="muted"`).
   *
   * 700 is not a choice so much as the floor's consequence: 14 IS Evan's micro floor, and every 14px run in this
   * scale is 700 or 800 because nothing lighter survives at that size. So the "quiet" in this line is carried by
   * the COLOUR — muted against the ink pill above it — and not by weight.
   *
   * Numerically identical to `pillSmQuiet` and kept as its own key for the reason `eyebrowSm` is: the call site
   * is named for the element it draws, so when this gets drawn properly it reverses by editing one object.
   */
  levelLine: { size: 14, weight: 700, lineHeight: lineHeights.headline },
  /** C18 header pill "47 earned" (board 13/800 — SHIPPED 14/800, micro floor; line box 15→16). */
  pillSm: { size: 14, weight: 800, lineHeight: lineHeights.headline },
  /** C18 header pill "19 to go", C04 filter chips (board 13/700 — SHIPPED 14/700, micro floor; line box 15→16). */
  pillSmQuiet: { size: 14, weight: 700, lineHeight: lineHeights.headline },
  /** C13/C14/C15 eyebrow, .16em caps (board 12/700 — SHIPPED 14/700, micro floor; line box 18→21, ls 1.92→2.24). */
  eyebrow: {
    size: 14,
    weight: 700,
    lineHeight: lineHeights.body,
    letterSpacingEm: 0.16,
    uppercase: true,
  },
  /**
   * C12 "KREDIT QUEST JR.", .16em caps (board 11/700 — SHIPPED 14/700, micro floor; line box 17→21).
   *
   * The raise makes this run IDENTICAL to `eyebrow`. The key is KEPT as a named alias rather than folded away,
   * because its one call site (`TrailScreen`) is named for the board element it draws and Evan's sweep may
   * re-split the two sizes. If it does, this reverses by editing one object.
   */
  eyebrowSm: {
    size: 14,
    weight: 700,
    lineHeight: lineHeights.body,
    letterSpacingEm: 0.16,
    uppercase: true,
  },
  /** C12 "ADVENTURE 2", .14em caps, muted (board 11/700 — SHIPPED 14/700, micro floor; line box 17→21, ls 1.54→1.96). */
  eyebrowQuiet: {
    size: 14,
    weight: 700,
    lineHeight: lineHeights.body,
    letterSpacingEm: 0.14,
    uppercase: true,
  },
  /**
   * C18 / C19 group labels, .1em caps (board 12/700 — SHIPPED 14/700, micro floor; line box 18→21, ls 1.2→1.4).
   * This is NO LONGER the same value as `parentType.sectionTitle`, which stays at the parent scale's 12.
   */
  sectionTitle: {
    size: 14,
    weight: 700,
    lineHeight: lineHeights.body,
    letterSpacingEm: 0.1,
    uppercase: true,
  },
  /** C15 "SAVER" under the medal (14/800, .06em, caps). */
  medalCaption: {
    size: 14,
    weight: 800,
    lineHeight: 1.2,
    letterSpacingEm: 0.06,
    uppercase: true,
  },
  /**
   * C12 node label "Mission 4 · Wants vs. needs", C11 "score" (board 13/700 — SHIPPED 16/700, content floor;
   * line box 20→24). THE LAST EXCEPTION, NOW CLOSED.
   *
   * Two things kept it at 13 and Evan settled both in one ruling: "the C12 current-mission pill is now anchored
   * 24px off the right edge, wraps instead of running off screen, and its caption is 16px. It does not collide
   * with the locked node. Size and layout landed together."
   *
   * • Which floor it takes was ambiguous — "Mission 4 · Wants vs. needs" is a sentence the child reads (16),
   *   C11's "score" is an axis label (14), and one run serves both. Evan named 16, so 16 it is; C11's "score"
   *   rides along at 16, which is above its own floor and therefore not a breach. If he wants that one back at
   *   14 it is a SPLIT of this run, not a lowering of it, and it has no call site today.
   * • Raising it used to widen the pill into the next trail node: the pill runs ~221px at 13, ~236px at 14 and
   *   ~266px at 16 on a 390pt device, against a `trailLayout.ts` that hangs it at `current.centerY + 56` on the
   *   current node's own side with the next node 72px down in the other column — ~49px of overlap at 16, painted
   *   on top. That layout is what Evan has now replaced (right-anchored, wrapping). Landing the wrap is
   *   `apps/mobile/src/features/quest`, another session's files this pass — filed in the round report.
   */
  label: { size: 16, weight: 700, lineHeight: lineHeights.body },
  /** C14 instruction "Tap the picture." (board 15/500 — SHIPPED 16/500, content floor; line box 23→24). */
  instruction: { size: 16, weight: 500, lineHeight: lineHeights.body },
  /** C15 "Show Mom my badge", C05 "Cancel my request" (board 15/700 — SHIPPED 16/700, content floor; 23→24). */
  link: { size: 16, weight: 700, lineHeight: lineHeights.body },
  /** C12 "3 of 6", C04 header pills (board 15/800 — SHIPPED 16/800, content floor; line box 17→18). */
  linkStrong: { size: 16, weight: 800, lineHeight: lineHeights.headline },
  /** C14 feedback explain, lh 1.4 (board 13/500 — SHIPPED 16/500, content floor; line box 18→22). */
  explain: { size: 16, weight: 500, lineHeight: 1.4 },
  /** C04 "Saving · 504 of 800", C11 "74 more" (board 11–12/700 — SHIPPED 14/700, micro floor). No call site yet. */
  progressLabel: { size: 14, weight: 700, lineHeight: lineHeights.body },
  /**
   * C18 tile caption, lh 1.2 (board 10/700 on streaks/firsts/quest and 9/700 on score bands — SHIPPED 16/700 for
   * ALL FOUR groups; line box 12→19). 16 is Evan's own C18 number, given verbatim, not a floor applied by
   * inference, and it is unchanged by his round-7 re-shape — the tile turned HORIZONTAL and two across so that
   * this 16 could hold all four groups above the fold, but the caption itself is the same run at the same size.
   * It is now drawn LEFT-aligned beside a 36px disc rather than centred under a 48px one (`badgeTileFace`).
   * `tileCaptionSm` is DELETED: the board's two caption sizes collapse into this one run, which is also why a
   * C18 tile no longer has a `group`.
   */
  tileCaption: { size: 16, weight: 700, lineHeight: 1.15 },
  /**
   * C01 child tab-bar label, the tab the child is on (board 13/700 on the 60px bar this app draws — and 12/700
   * on the 54px one — SHIPPED 14/700, micro floor). "Nav labels" is the FIRST category Evan's rule names, and
   * the child bar was the one place still rendering off the parent scale (`parentType.labelStrong` 12/700).
   *
   * C09's pre-reader bar (68px discs) already draws 14/700, which is exactly this run, so `prereaderType`
   * needs no override: the floor and the pre-reader board value agree here.
   */
  navLabelStrong: { size: 14, weight: 700, lineHeight: lineHeights.body },
  /** C01 child tab-bar label, idle (board 13/600 — SHIPPED 14/600, micro floor; C09 draws 14/600). */
  navLabel: { size: 14, weight: 600, lineHeight: lineHeights.body },
  /**
   * C18 EARNED disc number "60", the C18 unearned TARGET line, and the C19 Oops disc "−3" (board 13/800 there)
   * — one run at 14/800, which is exactly Evan's "14px target".
   *
   * Three sites, not two, since his round-7 re-shape: the target used to be drawn INSIDE an unearned disc and is
   * now a line UNDER the caption, so this run is no longer only a disc run. `lineHeight: 1` stays — it is what
   * centres a glyph in a disc, and under the caption it is a single line whose spacing `BadgeTile` sets with a
   * 2px column gap rather than with leading.
   */
  discNumber: { size: 14, weight: 800, lineHeight: 1 },
  /**
   * C18 adventure tag "A2" and the C18 / C19 org tag "BGC" (board 12/800 and 10/800 — SHIPPED 14/800 for both,
   * micro floor; a tag is the rule's own example). `discTagSm` is DELETED with `faces.ts`'s `tagVariant`: that
   * ladder was two board sizes, not shrink-to-fit, and 10/800 is exactly the defect Evan named. Keep
   * lineHeight 1 — it is what centres the glyph in the disc.
   */
  discTag: { size: 14, weight: 800, lineHeight: 1 },
} as const satisfies Record<string, TypeStyle>;

export type ChildVariant = keyof typeof childType;

/**
 * Pre-reader overrides. Only the variants a pre-reader artboard actually draws (C09–C11, T02) are listed:
 * the board has no pre-reader C12–C15, C18 or C19, so every other variant keeps its reader size and the
 * band's enlargement is the hit target (`childHit`). Do not extend this table by guesswork — the missing
 * pre-reader values are a design ask, not an extrapolation.
 */
export const prereaderType = {
  /** C11 tile title and price (18/800) against C04's 15/700. */
  messageTitle: { size: 18, weight: 800, lineHeight: lineHeights.headline },
  /** C11 "Get" (18/800) against C04's "Get it" 14/700. */
  buttonSm: { size: 18, weight: 800, lineHeight: lineHeights.headline },
  /** C11 BB pill "126" (22/800) against C04's 15/800. */
  linkStrong: { size: 22, weight: 800, lineHeight: 1.1 },
  /** C11 header "Rewards" (30/800) against C04's 28/800. */
  title: { size: 30, weight: 800, lineHeight: lineHeights.headline },
} as const satisfies Partial<Record<ChildVariant, TypeStyle>>;

/** The child scale for a band: reader as drawn, pre-reader with the C09–C11 overrides applied. */
export function childTypeFor(band: ChildBand): Record<ChildVariant, TypeStyle> {
  return band === 'prereader' ? { ...childType, ...prereaderType } : childType;
}

export type ParentVariant = keyof typeof parentType;

/**
 * **THE TALLEST-TO-DEEPEST INK POPPINS CAN DRAW, MEASURED FROM EVERY SHIPPED WEIGHT.**
 *
 * Read out of the `glyf` tables of all eighteen `@expo-google-fonts/poppins` files over the character set this
 * app uses (ASCII plus `· — – … ▲ − ’ “ ” $ £ € %`):
 *
 *   · highest glyph  **`/` at 0.955 em** in Poppins 900 Black — not a letter, and the app renders it as "/100"
 *     on every score screen
 *   · deepest glyph  **`g` at −0.277 em**
 *   · worst-case extent **1.232 em**, in 800 ExtraBold and 900 Black
 *
 * **THIS REPLACES AN ASCENT-ONLY FLOOR OF 1.135 em, WHICH WAS SHORT BY 0.097 em** — and short for a reason
 * worth keeping. That floor was justified here as *"display numerals have no descenders"*, which is false twice
 * over: `$` descends 0.087 em (it is in "$50.00", the string the clipping was reported on), and the floor keys
 * on SIZE, so it caught fourteen styles of which several render text — `rowTitle` draws parent-typed chore
 * titles and free prose, `messageTitle` draws "Tap your panda". Evan caught it by reading the justification
 * rather than the code: *"the floor applies to fourteen styles by size, not by content."*
 *
 * **One floor rather than one per purpose**, which was the alternative: a style-by-style classification of
 * eighty-two variants into "numerals" and "text" is a second thing to keep true, and free text can appear in
 * anything. A box that fits the font's worst case fits every string in it.
 */
const POPPINS_INK_EXTENT_EM = 1.232;

/**
 * **THE FLOOR IS WHY GLYPHS STOPPED BEING CLIPPED** (Evan, device walk 2026-09-21): *"'$50.00' on P13f and the
 * balance on C06 — the tops of the digits and the dollar sign are shaved."*
 *
 * His hypothesis was that the scale set `lineHeight` equal to `fontSize`, the Figma convention. It does not —
 * these are RATIOS, and `balance` is 1.05 — but the mechanism was right, and measuring the shipped font rather
 * than accepting the detail turned two visible breakages into forty-four short variants.
 *
 * React Native on iOS lays out from the line box and clips what will not fit. It raises no error and looks like
 * a font choice until someone holds the phone, which is why `typography.test.ts` asserts the invariant rather
 * than the numbers.
 */
/**
 * **A SANCTIONED SHRINK, NOT A FONT OVERRIDE** (Evan, 30 Sep). A Spanish label that will not fit on one line
 * may come down to 80% — see `fitSteps.ts` — and `SafeTextStyle` rightly forbids a caller passing `fontSize`.
 * So the scale is an argument here, applied to the size and everything derived from it, and every derived
 * value is recomputed rather than scaled: a line height rounded and then multiplied is not the line height of
 * the smaller size.
 */
export function textStyleFor(t: TypeStyle, scale = 1): TextStyle {
  if (scale !== 1) return textStyleFor({ ...t, size: Math.round(t.size * scale) });
  return {
    ...fontStyle(t.weight),
    fontSize: t.size,
    lineHeight: Math.max(
      Math.round(t.size * t.lineHeight),
      Math.ceil(t.size * POPPINS_INK_EXTENT_EM),
    ),
    letterSpacing: t.letterSpacingEm ? t.letterSpacingEm * t.size : undefined,
    textTransform: t.uppercase ? 'uppercase' : undefined,
    // Android adds ascender/descender padding that sinks Poppins glyphs in centred containers.
    ...(Platform.OS === 'android'
      ? { includeFontPadding: false, textAlignVertical: 'center' as const }
      : {}),
  };
}
